import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Paper, Button, TextField } from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

interface PaginationBarProps {
  currentPage: number;
  totalPages: number;
  totalResults?: number;
  disabled?: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onPageChange?: (page: number) => void;
}

const PaginationBar: React.FC<PaginationBarProps> = ({
  currentPage,
  totalPages,
  totalResults,
  disabled = false,
  onPrevious,
  onNext,
  onPageChange,
}) => {
  const showTotalResults = totalResults !== undefined;
  const [inputValue, setInputValue] = useState<string>(String(currentPage));

  // Sync input value when currentPage changes externally
  useEffect(() => {
    setInputValue(String(currentPage));
  }, [currentPage]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  }, []);

  const handleGoToPage = useCallback(() => {
    const pageNum = parseInt(inputValue, 10);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages && pageNum !== currentPage) {
      onPageChange?.(pageNum);
    } else {
      // Reset to current page if invalid
      setInputValue(String(currentPage));
    }
  }, [inputValue, totalPages, currentPage, onPageChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleGoToPage();
    }
  }, [handleGoToPage]);

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2">
          {totalPages > 0 ? 'Page' : 'No results found'}
        </Typography>
        {totalPages > 0 && (
          <>
            <TextField
              size="small"
              type="number"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              disabled={disabled || totalPages <= 1}
              inputProps={{
                min: 1,
                max: totalPages,
                style: { textAlign: 'center', width: '50px', padding: '4px 8px' },
              }}
              sx={{ width: '70px' }}
            />
            <Typography variant="body2">
              of {totalPages}
              {showTotalResults && ` (${totalResults.toLocaleString()} total results)`}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={handleGoToPage}
              disabled={disabled || totalPages <= 1}
              sx={{ minWidth: 'auto', px: 1.5 }}
            >
              Go
            </Button>
          </>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<NavigateBeforeIcon />}
          onClick={onPrevious}
          disabled={currentPage <= 1 || disabled}
        >
          Previous
        </Button>
        <Button
          size="small"
          variant="outlined"
          endIcon={<NavigateNextIcon />}
          onClick={onNext}
          disabled={currentPage >= totalPages || disabled}
        >
          Next
        </Button>
      </Box>
    </Paper>
  );
};

export default React.memo(PaginationBar);
